import { useContext } from 'react';
import Axios from 'axios';
import I18n from '@/modules/i18n';
import { plato_api_reorder_slide_group_path } from '@/modules/routes';
import showToast from '@/components/common/Toast';
import { SlideShowContext } from '@/components/admin/SlideShow/stores/SlideShowStoreProvider';
import { moveGroup, orderedGroupIds } from '@/components/admin/SlideShow/stores/slideGroupOrder';
import { SlideGroup } from '@/components/admin/SlideShow/Builder/CreateTransistionGroup/utils';

interface ReorderResponse {
  slideGroups: SlideGroup[];
  slides: { id: string; position: number; slideGroupId: number | null }[];
}

type Id = number | string;

// Used by the Group Slides popup table. Applies the move to the store first, persists it, then merges the
// server's positions back the same way the sidebar's slide drag does.
const useReorderSlideGroup = () => {
  const store = useContext(SlideShowContext);

  // Resolves to whether the move was saved, so callers can skip success feedback after a rollback.
  const moveGroupTo = async (id: Id, targetPosition: number): Promise<boolean> => {
    const state = store.getState();
    const currentOrder = orderedGroupIds(state.slideShow.slides, state.slideShow.slideGroups);
    const currentPosition = currentOrder.indexOf(String(id)) + 1;
    if (currentPosition === 0 || currentPosition === targetPosition) return false;

    const snapshot = { slideGroups: state.slideShow.slideGroups, slides: state.slideShow.slides };
    state.reorderSlideGroups(moveGroup(currentOrder, id, targetPosition));

    try {
      const { data } = await Axios.patch<ReorderResponse>(
        plato_api_reorder_slide_group_path(id),
        { position: targetPosition },
      );

      const updatesById = data.slides.reduce((acc, slide) => {
        acc[slide.id] = { position: slide.position, slideGroupId: slide.slideGroupId };

        return acc;
      }, {} as Record<string, { position: number; slideGroupId: number | null }>);

      const slides = store.getState().slideShow.slides
        .map(slide => ({ ...slide, ...updatesById[String(slide.id)] }))
        .sort((a, b) => a.position - b.position);

      store.getState().updateSlideShow({ slideGroups: data.slideGroups, slides });

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      store.getState().updateSlideShow(snapshot);
      showToast(I18n.t('error_reordering_slide_group'), { msgType: 'error' });

      return false;
    }
  };

  return { moveGroupTo };
};

export default useReorderSlideGroup;
